import React, { useContext, useMemo } from 'react'
import { getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import DataTable from '../../../Components/shared/DataTable/DataTable'
import { User, getEmail, getFirstname, getInstitutionsName, getLanguage, getLastConnection, getLastname, getRole, getUsername } from '../../../utils/entities/user'
import { ACTIONS, Action, DeleteAction } from '../../../Components/shared/DataTable/utils/actions'
import { faEdit, faGraduationCap, faKey, faStethoscope } from '@fortawesome/free-solid-svg-icons'
import { MACCS_CONTEXT, MACCS_ENVIRONMENT, ORDER_BY, ROLE_INSTITUTION_GROUP_ADMIN, ROLE_LOCAL_SECRETARY } from '../../../utils/constants'
import Cookie from 'js-cookie'
import { InstitutionGroupContext } from '../../../Providers/InstitutionGroupProvider'
import { Filter, buildFilters } from '../../../Components/shared/DataTable/utils/tableFilter'
import SelectInstitutionFilter from '../../../Components/shared/DataTable/utils/SelectInstitutionFilter'
import { GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import { getUser } from '../../../reducers/UserReducer'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { recursiveIncludes } from '../../../utils'

const mapStateToProps = state => {
  return { t: getTranslate(state.locale), user: getUser(state.getUser) }
}

const UsersTable = ({ loading, parameters, user, users, onParametersChange, onTriggerAction, t }) => {
  const { environment } = useContext(GlobalContext)
  const institutionGroupContext = useContext(InstitutionGroupContext)
  const { selectedInstitution, setSelectedInstitution } = useContext(GlobalFiltersContext)

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '240px' : '199px'})`, [Cookie.get('switch_user_id')])
  const columns = useMemo(() => [
    { title: t('users_view.column_title.email'), orderBy: ORDER_BY.EMAIL, key: getEmail },
    { title: t('users_view.column_title.username'), orderBy: ORDER_BY.USERNAME, key: getUsername },
    { title: t('users_view.column_title.lastname'), orderBy: ORDER_BY.LASTNAME, key: getLastname },
    { title: t('users_view.column_title.firstname'), orderBy: ORDER_BY.FIRSTNAME, key: getFirstname },
    { title: t('users_view.column_title.language'), orderBy: ORDER_BY.LANGUAGE, key: getLanguage },
    { title: t('users_view.column_title.role'), orderBy: ORDER_BY.ROLE, key: u => t(getRole(u)) },
    {
      title: t('users_view.column_title.institution'),
      orderBy: ORDER_BY.INSTITUTION,
      key: u => {
        const name = getInstitutionsName(u)

        return name.length > 25 ? `${name.slice(0, 22)}...` : name
      },
      tooltip: getInstitutionsName
    },
    { title: t('users_view.column_title.last_connection'), orderBy: ORDER_BY.LAST_CONNECTION, key: getLastConnection }
  ], [t, getEmail, getUsername, getLastname, getFirstname, getLanguage, getRole, getInstitutionsName])
  const actions = useMemo(() => {
    const actions = [new Action({ title: 'users_view.actions.edit_user', icon: faEdit, onClick: u => onTriggerAction(u, ACTIONS.EDIT) })]

    if (user.context === MACCS_CONTEXT || (user.roles.includes(ROLE_INSTITUTION_GROUP_ADMIN) && environment === MACCS_ENVIRONMENT)) {
      actions.push(new Action({
        title: 'users_view.actions.display_user_specialities',
        icon: faGraduationCap,
        onClick: u => onTriggerAction(u, ACTIONS.DISPLAY_USER_SPECIALITIES),
        disabled: d => recursiveIncludes(d.roles, [ROLE_INSTITUTION_GROUP_ADMIN, ROLE_LOCAL_SECRETARY])
      }))
    } else {
      actions.push(new Action({
        title: 'users_view.actions.set_user_coordinate_sectors',
        icon: faStethoscope,
        onClick: u => onTriggerAction(u, ACTIONS.DISPLAY_USER_SECTORS),
        disabled: d => d.roles.includes(ROLE_INSTITUTION_GROUP_ADMIN)
      }))
    }

    actions.push(
      new Action({ title: 'users_view.actions.reset_user_password', icon: faKey, onClick: u => onTriggerAction(u, ACTIONS.RESET_USER_PASSWORD) }),
      new DeleteAction({ onDelete: u => onTriggerAction(u, ACTIONS.DELETE) })
    )

    return actions
  }, [onTriggerAction])
  const filters = useMemo(() => buildFilters([
    new Filter({
      key: 'institution',
      label: t('Institution'),
      component: SelectInstitutionFilter,
      props: { institutions: institutionGroupContext?.institutions, value: selectedInstitution, onChange: setSelectedInstitution }
    })
  ]), [institutionGroupContext?.institutions, selectedInstitution, setSelectedInstitution, t])

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        actions={actions}
        columns={columns}
        data={users.data}
        filters={filters}
        loading={loading}
        metadata={users.meta}
        parameters={parameters}
        onCreate={() => onTriggerAction(new User(), ACTIONS.CREATE)}
        onParametersChange={onParametersChange}
      />
    </div>
  )
}

export default connect(mapStateToProps)(UsersTable)
